/**
 * The section the Escalation Configuration page renders — the source of truth
 * for rendering and validation. The parser (wire → model) lives separately in
 * `@/lib/utils/clinic-configuration/kora/kora-parsers.ts`; this file holds
 * only data description, no runtime logic.
 *
 * Page schema, not resource schema: the section is stored on the same
 * `Basic` as the Kora Behavior and Follow-ups sections, and
 * `clinicConfigurationSchema` is the whole of it.
 *
 * Every field is optional, mirroring `min=0` on every slice of the
 * `escalation-configuration` StructureDefinition in the backend's
 * `fhir-toolkit/src/fhir_toolkit/fhir_extensions.py`. A clinic that has
 * never saved the page has no such extension, which reads as every field
 * unset. See `kora-config.schema.ts` for why nothing on the server enforces
 * the profile.
 */
import LifeBuoy from "lucide-react/dist/esm/icons/life-buoy";

import type { ConfigSchema } from "@/types/clinic-configuration";
import {
  FHIR_EXTENSION_URLS,
  FHIR_EXTENSIONS,
} from "@/constants/fhir.constants";
import { ROUTES } from "@/constants/routes.constants";

/**
 * Needs for i18next-scanner
 */
// t('Escalation')
// t('Escalation Configuration')
// t('How Kora hands a conversation to a human, and how a patient can book without Kora')
// t('Escalator guidelines')
// t("Clinic-specific rules added to Kora's response when a patient is escalated for a non-scheduling reason (urgent care, complaint, financial hardship)")
// t('Human scheduling guidelines')
// t('Clinic-specific rules added when Kora confirms a coordinator will call back to schedule')
// t('Self-scheduling link')
// t('Whether the clinic uses a self-service scheduling link instead of a coordinator callback. When on, Kora uses the provider, link and link guidelines. When off, Kora uses the human scheduling guidelines')
// t('Kora can share the link')
// t("Kora doesn't share the link")
// t('Scheduling provider')
// t('Name of the self-service scheduling tool referenced in the link message, such as Google Calendar, Calendly, Doctoralia or Linktree. Only used when the self-scheduling link is on')
// t('Link URL')
// t("Self-service scheduling URL shared with patients. Only used when the self-scheduling link is on")
// t('Link guidelines')
// t('Clinic-specific rules added when Kora shares a self-service scheduling link (Calendly / Google Calendar)')
// t('Offer a callback')
// t("Whether to offer a coordinator callback if the patient doesn't want to use the link. Only used when the self-scheduling link is on")
// t('Kora offers a callback as a fallback')
// t("Kora doesn't offer a callback")
export const escalationConfigurationSchema = {
  extensions: {
    escalationConfiguration: {
      kind: "object",
      slug: FHIR_EXTENSIONS.KORA_CONFIG_ESCALATION_CONFIGURATION,
      title: "Escalation Configuration",
      description:
        "How Kora hands a conversation to a human, and how a patient can book without Kora",
      fields: {
        escalatorClinicGuidelines: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_ESCALATION_CONFIGURATION
            .ESCALATOR_CLINIC_GUIDELINES,
          title: "Escalator guidelines",
          description:
            "Clinic-specific rules added to Kora's response when a patient is escalated for a non-scheduling reason (urgent care, complaint, financial hardship)",
          layout: "stacked",
        },
        humanSchedulingClinicGuidelines: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_ESCALATION_CONFIGURATION
            .HUMAN_SCHEDULING_CLINIC_GUIDELINES,
          title: "Human scheduling guidelines",
          description:
            "Clinic-specific rules added when Kora confirms a coordinator will call back to schedule",
          layout: "stacked",
        },
        schedulingLinkEnabled: {
          kind: "boolean",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_ESCALATION_CONFIGURATION
            .SCHEDULING_LINK_ENABLED,
          title: "Self-scheduling link",
          description:
            "Whether the clinic uses a self-service scheduling link instead of a coordinator callback. When on, Kora uses the provider, link and link guidelines. When off, Kora uses the human scheduling guidelines",
          presentation: {
            trueLabel: "Kora can share the link",
            falseLabel: "Kora doesn't share the link",
          },
        },
        schedulingProvider: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_ESCALATION_CONFIGURATION
            .SCHEDULING_PROVIDER,
          title: "Scheduling provider",
          requiredWhen: "schedulingLinkEnabled",
          description:
            "Name of the self-service scheduling tool referenced in the link message, such as Google Calendar, Calendly, Doctoralia or Linktree. Only used when the self-scheduling link is on",
        },
        schedulingLink: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_ESCALATION_CONFIGURATION
            .SCHEDULING_LINK,
          title: "Link URL",
          requiredWhen: "schedulingLinkEnabled",
          description:
            "Self-service scheduling URL shared with patients. Only used when the self-scheduling link is on",
          validation: { format: "url" },
        },
        schedulingLinkClinicGuidelines: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_ESCALATION_CONFIGURATION
            .SCHEDULING_LINK_CLINIC_GUIDELINES,
          title: "Link guidelines",
          description:
            "Clinic-specific rules added when Kora shares a self-service scheduling link (Calendly / Google Calendar)",
          layout: "stacked",
        },
        offerCallbackAsFallback: {
          kind: "boolean",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_ESCALATION_CONFIGURATION
            .OFFER_CALLBACK_AS_FALLBACK,
          title: "Offer a callback",
          description:
            "Whether to offer a coordinator callback if the patient doesn't want to use the link. Only used when the self-scheduling link is on",
          presentation: {
            trueLabel: "Kora offers a callback as a fallback",
            falseLabel: "Kora doesn't offer a callback",
          },
        },
      },
    },
  },
} as const satisfies ConfigSchema.Resource;

export const escalationConfigurationPage = {
  to: ROUTES.AGENT_ESCALATION_CONFIGURATION,
  /**
   * The rail's label, kept short on purpose. The page itself is titled by the
   * section ("Escalation Configuration"), which reads fine as a heading but
   * is too long for the rail.
   */
  label: "Escalation",
  icon: LifeBuoy,
} satisfies ConfigSchema.Page;
